import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  BrushCleaning,
  Loader2,
  MessageCircleHeart,
  MessageSquare,
  Send,
  X,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import {
  ensureAuthorized,
  getAccessToken,
  openAuthorizePopup,
} from '../../../../lib/ringcentral/auth';
import {
  fetchNudgeDraft,
  useSendNudge,
} from '../../../../services/onboardingBoard';

// Text them (Nic 2026-09-24): the message is already written for where the
// lead is (name + the right link); the rep reads it, edits if they like,
// and sends it from their own RingCentral line.
// Reusable (2026-09-28): the Client Onboarding page passes its own
// loadDraft / sendText and name; the lead is the default.

const MAX = 1000;

const NudgeComposer = ({
  open,
  lead,
  onClose,
  // Optional overrides (Client Onboarding): loadDraft() -> { text, optedOut,
  // hasPhone }, sendText({ text, rcAccessToken }), draftKey (reloads on
  // change), name, sending.
  loadDraft = null,
  sendText = null,
  draftKey = null,
  name: nameOverride = null,
  sending = false,
  // Blank (Nic 2026-10-07): the Workspace's Text starts empty; the written
  // message comes only from a card's Nudge.
  blank = false,
}) => {
  const [text, setText] = useState('');
  const boxRef = useRef(null);
  const [meta, setMeta] = useState(null);
  // Which draft is on screen. Loading is derived from it, so the very first
  // frame already shows the waiting box (Nic 2026-09-30: an empty box, then a
  // spinner, then the text read as flashing twice).
  const [loadedKey, setLoadedKey] = useState(null);
  const send = useSendNudge();
  const leadId = lead?.leadId;
  const key = draftKey || leadId;
  const loading = loadedKey !== key;
  const name =
    nameOverride ||
    [lead?.leadFirstName, lead?.leadLastName].filter(Boolean).join(' ') ||
    'this lead';

  useEffect(() => {
    if (!open || !key) {
      setLoadedKey(null);
      return undefined;
    }
    let live = true;
    (loadDraft ? loadDraft() : fetchNudgeDraft(leadId))
      .then((d) => {
        if (!live) return;
        setText(blank ? '' : d.text || '');
        setMeta(d);
      })
      .catch(() => {
        if (!live) return;
        setText('');
        setMeta(null);
        toast.error("The message couldn't be prepared.");
      })
      .finally(() => live && setLoadedKey(key));
    return () => {
      live = false;
    };
    // loadDraft is keyed by draftKey; a new function identity is not a new draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key]);

  // A blank box is for writing: put the cursor in it once it shows.
  useEffect(() => {
    if (open && blank && !loading) boxRef.current?.focus();
  }, [open, blank, loading]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const blocked = meta?.optedOut || meta?.hasPhone === false;
  const busy = sendText ? sending : send.isLoading;

  const doSend = async () => {
    let authorized = await ensureAuthorized();
    if (!authorized) authorized = await openAuthorizePopup();
    if (!authorized) {
      toast.error(
        'Log into RingCentral to send texts. Use the RingCentral chip in the board header.',
      );
      return;
    }
    try {
      if (sendText) {
        await sendText({ text: text.trim(), rcAccessToken: getAccessToken() });
      } else {
        await send.mutateAsync({
          leadId,
          text: text.trim(),
          rcAccessToken: getAccessToken(),
        });
      }
      toast.success('Sent.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "The text didn't go out.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1085] flex items-end justify-center p-3 sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:!bg-surface-elevated">
        <div className="flex items-start gap-3 border-b border-slate-100 px-[18px] py-4 dark:border-white/10">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 dark:!bg-sky-500/15 dark:text-sky-300">
            {/* A nudge wears the card's Nudge mark; a blank text is a plain bubble. */}
            {blank ? (
              <MessageSquare size={17} />
            ) : (
              <MessageCircleHeart size={17} />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-800 dark:text-slate-100">
              Text {name}
            </div>
            <div className="truncate text-xs text-slate-500 dark:text-slate-400">
              Sends from your own line
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-400 outline-none hover:bg-slate-100 focus:outline-none dark:hover:!bg-white/10"
          >
            <X size={17} />
          </button>
        </div>

        <div className="px-[18px] py-4">
          {loading ? (
            <div className="flex h-[150px] items-center justify-center">
              <Loader2 size={18} className="animate-spin text-slate-400" />
            </div>
          ) : blocked ? (
            <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:!bg-amber-500/10 dark:text-amber-200">
              {meta?.optedOut
                ? 'They replied STOP, so texts are off. Call or email instead.'
                : 'There is no phone number on file. Email them instead.'}
            </div>
          ) : (
            <>
              <div className="relative">
                <textarea
                  ref={boxRef}
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, MAX))}
                  rows={5}
                  // border-[1px], not border: Bootstrap's .border forces its
                  // own light color with !important. pr-12 keeps the words
                  // clear of the wipe button.
                  className="w-full resize-none rounded-xl border-[1px] border-slate-200 bg-white py-3 pl-3.5 pr-12 text-base leading-relaxed text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 sm:text-sm dark:border-white/10 dark:!bg-white/5 dark:text-slate-100"
                />
                {/* Wipe it clean to write your own (Nic 2026-10-05). */}
                {text ? (
                  <button
                    type="button"
                    onClick={() => {
                      setText('');
                      boxRef.current?.focus();
                    }}
                    title="Clear the text"
                    aria-label="Clear the text"
                    className="absolute right-4 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-rose-600 shadow-sm ring-1 ring-rose-200 outline-none transition-[background-color,color,transform] hover:scale-105 hover:bg-rose-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 dark:!bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/30 dark:hover:!bg-rose-600 dark:hover:text-white"
                  >
                    <BrushCleaning size={14} />
                  </button>
                ) : null}
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                <span>Opt-out wording is added automatically.</span>
                <span className="tabular-nums">
                  {text.length}/{MAX}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-[18px] py-3 dark:border-white/10 dark:!bg-white/[0.02]">
          <span className="mr-auto" />
          {!blocked ? (
            <button
              type="button"
              disabled={busy || loading || !text.trim()}
              onClick={doSend}
              className={cn(
                'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-sky-600 px-4 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-sky-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {busy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              Send text
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NudgeComposer;
